import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Card, CardContent, CardHeader, Stack } from '@packages/shared';
import { useIntl } from 'react-intl';
import { ServiceInformationCardAdditionalCostsFragmentFragmentDoc } from '@packages/gql/generated/shopping/ServiceInformationCardAdditionalCostsFragmentFragmentDoc';
import { ServiceInformationCardDeliveryFragmentFragmentDoc } from '@packages/gql/generated/shopping/ServiceInformationCardDeliveryFragmentFragmentDoc';
import { InDelivery32, Paymentmethods32, Return32 } from '@packages/themes/icons';
import { useConfig } from '@packages/utilities';
import {
  additionalCostsMessages,
  deliveryMessages,
  paymentMethodsMessages,
  serviceInformationCardMessages,
} from './messages';
import { AdditionalInfoItem } from '../AdditionalInfoItem';
import { getValidInfoList } from '../getValidInfoList';

/* GraphQL */ `
  fragment ServiceInformationCardAdditionalCostsFragment on AdditionalCostsInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;
/* GraphQL */ `
  fragment ServiceInformationCardDeliveryFragment on DeliveryInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;

export type ServiceInformationCardProps = {
  additionalCostsData: FragmentType<
    typeof ServiceInformationCardAdditionalCostsFragmentFragmentDoc
  > | null;
  deliveryData: FragmentType<typeof ServiceInformationCardDeliveryFragmentFragmentDoc> | null;
};

const iconSize = '1.5rem';
const iconColor = 'text.dark';

export const ServiceInformationCard = ({
  additionalCostsData,
  deliveryData,
}: ServiceInformationCardProps) => {
  const { formatMessage } = useIntl();
  const {
    product: {
      serviceLayer: { paymentMethods },
    },
  } = useConfig();

  if (additionalCostsData === null && deliveryData === null && paymentMethods === null) return null;

  const additionalCosts = getFragmentData(
    ServiceInformationCardAdditionalCostsFragmentFragmentDoc,
    additionalCostsData,
  );
  const delivery = getFragmentData(ServiceInformationCardDeliveryFragmentFragmentDoc, deliveryData);

  if (
    additionalCosts?.infoList.length === 0 &&
    delivery?.infoList.length === 0 &&
    paymentMethods === null
  ) {
    return null;
  }

  const validAdditionalCostsInfoList = additionalCosts
    ? getValidInfoList(additionalCosts.infoList, additionalCostsMessages)
    : [];
  const validDeliveryInfoList = delivery
    ? getValidInfoList(delivery.infoList, deliveryMessages)
    : [];

  if (
    validAdditionalCostsInfoList.length === 0 &&
    validDeliveryInfoList.length === 0 &&
    paymentMethods === null
  )
    return null;

  return (
    <Card>
      <CardHeader
        title={formatMessage(serviceInformationCardMessages.cardHeadline)}
        sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
      />
      <CardContent>
        <Stack spacing={1} alignItems="flex-start">
          {paymentMethods !== null && (
            <AdditionalInfoItem
              icon={
                <Paymentmethods32 sx={{ color: iconColor, width: iconSize, height: iconSize }} />
              }
              serviceLayerId={paymentMethods}
              text={formatMessage(paymentMethodsMessages.paymentMethods)}
            />
          )}
          {validDeliveryInfoList.map((item, index) => (
            <AdditionalInfoItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              icon={
                item!.iconUrl || (
                  <InDelivery32 sx={{ color: iconColor, width: iconSize, height: iconSize }} />
                )
              }
              serviceLayerId={item!.serviceLayerId}
              text={formatMessage(item.messageDescriptor)}
            />
          ))}
          {validAdditionalCostsInfoList.map((item, index) => (
            <AdditionalInfoItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              icon={
                item!.iconUrl || (
                  <Return32 sx={{ color: iconColor, width: iconSize, height: iconSize }} />
                )
              }
              serviceLayerId={item!.serviceLayerId}
              text={formatMessage(item.messageDescriptor)}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
